// src/modules/home/components/slots/DAGViewerSlot.jsx
// ─────────────────────────────────────────────────────────────────────────────
// DAG VIEWER — Interactive Physics-based Knowledge Graph
//
// Canvas-rendered force-directed graph with:
//   • Glowing orb nodes with radial gradients & breathing animation
//   • Curved edges with flowing particle effects (data-flow vis)
//   • Draggable nodes with spring-back physics
//   • Ripple burst on new actions
//   • Ambient particle field background
//   • Hover → expand node + tooltip with details
//   • Mesmerizing empty-state orbital animation
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { T } from '../TopBar';
import {
  buildDAGTree, extractPipeline, savePipeline,
  loadSession, saveSession, formatRelTime, getModuleInfo, getUserKey,
} from '../../../utils/actionStore';

const MONO = 'JetBrains Mono,monospace';
const PI2 = Math.PI * 2;
const CANVAS_H = 360;

// ─── Group colors ────────────────────────────────────────────────────────────
const GC = {
  data: '#22d3ee', clean: '#4ade80', explore: '#fbbf24', report: '#a78bfa',
  model: '#f97316', train: '#fb923c', eval: '#38bdf8', nlp: '#c084fc',
  vision: '#fbbf24', config: '#34d399', run: '#34d399', misc: '#64748b',
  audio: '#4ade80', transcribe: '#38bdf8', analyze: '#f97316', video: '#f472b6',
};

// ─── Physics ─────────────────────────────────────────────────────────────────
const PH = {
  gravity: 0.009, repel: 2600, springK: 0.032, springLen: 72,
  damp: 0.88, maxV: 3.8,
};
const BASE_R = 18;
const ROOT_R = 27;

// ─── Helpers ─────────────────────────────────────────────────────────────────
function h2r(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

// ─── Empty state renderer ───────────────────────────────────────────────────
function drawEmpty(ctx, w, h, t, accentHex) {
  const cx = w / 2, cy = h / 2 - 8;
  // Orbital rings
  for (let i = 0; i < 4; i++) {
    const r = 28 + i * 24;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, PI2);
    ctx.strokeStyle = h2r(accentHex, 0.055 - i * 0.01);
    ctx.lineWidth = 1;
    ctx.stroke();
    // Orbiting dot
    const a = t * (0.7 - i * 0.14) + i * 1.8;
    const dx = cx + Math.cos(a) * r, dy = cy + Math.sin(a) * r;
    const gr = ctx.createRadialGradient(dx, dy, 0, dx, dy, 8);
    gr.addColorStop(0, h2r(accentHex, 0.55));
    gr.addColorStop(1, h2r(accentHex, 0));
    ctx.beginPath(); ctx.arc(dx, dy, 8, 0, PI2); ctx.fillStyle = gr; ctx.fill();
    ctx.beginPath(); ctx.arc(dx, dy, 2.5 - i * 0.3, 0, PI2);
    ctx.fillStyle = h2r(accentHex, 0.7); ctx.fill();
  }
  // Central glow
  const p = Math.sin(t * 1.4) * 0.18 + 1;
  const cg = ctx.createRadialGradient(cx, cy, 0, cx, cy, 22 * p);
  cg.addColorStop(0, h2r(accentHex, 0.3)); cg.addColorStop(1, h2r(accentHex, 0));
  ctx.beginPath(); ctx.arc(cx, cy, 22 * p, 0, PI2); ctx.fillStyle = cg; ctx.fill();
  ctx.beginPath(); ctx.arc(cx, cy, 5, 0, PI2);
  ctx.fillStyle = h2r(accentHex, 0.55); ctx.fill();
  // Floating text
  ctx.textAlign = 'center';
  ctx.font = `800 8.5px ${MONO}`;
  ctx.fillStyle = h2r(accentHex, 0.4);
  ctx.fillText('KNOWLEDGE GRAPH', cx, cy + 56);
  ctx.font = `500 7.5px ${MONO}`;
  ctx.fillStyle = 'rgba(148,163,184,0.3)';
  ctx.fillText('Upload data & start working to see it evolve', cx, cy + 70);
}

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────
export default function DAGViewerSlot({ activeModule, authUser, session, onSessionChange }) {
  const userKey = getUserKey(authUser);
  const moduleInfo = getModuleInfo(activeModule);
  const accentHex = moduleInfo.color;
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const stRef = useRef(null);            // physics state
  const animRef = useRef(0);
  const [tooltip, setTooltip] = useState(null);
  const prevModuleRef = useRef(activeModule);

  const allNodes = session.nodes || [];
  const actionNodes = useMemo(
    () => allNodes.filter((n) => n.module === activeModule),
    [allNodes, activeModule]
  );
  const dagTree = useMemo(() => buildDAGTree(actionNodes), [actionNodes]);

  // ── Init physics state (once) ───────────────────────────────────────────
  if (!stRef.current) {
    stRef.current = {
      nodes: [], edges: [], ripples: [],
      ambient: [],       // background particles
      mouse: { x: 0, y: 0, down: false, drag: -1, hover: -1 },
      t: 0, w: 300, h: CANVAS_H, synced: 0,
    };
  }
  const S = stRef.current;

  // ── Sync action nodes → physics nodes ────────────────────────────────────
  useEffect(() => {
    if (prevModuleRef.current !== activeModule) {
      S.nodes = []; S.edges = []; S.ripples = []; S.synced = 0;
      S.mouse.hover = -1; S.mouse.drag = -1; S.mouse.down = false;
      setTooltip(null);
      prevModuleRef.current = activeModule;
    }
    const cx = S.w / 2, cy = S.h / 2;
    for (let i = S.synced; i < actionNodes.length; i++) {
      const a = actionNodes[i];
      const isRoot = a.group === 'data';
      const par = i > 0 ? S.nodes[i - 1] : null;
      const sx = par ? par.x + (Math.random() - 0.5) * 40 : cx + (Math.random() - 0.5) * 50;
      const sy = par ? par.y + (Math.random() - 0.5) * 40 : cy + (Math.random() - 0.5) * 50;
      S.nodes.push({
        x: sx, y: sy, vx: (Math.random() - 0.5) * 2, vy: (Math.random() - 0.5) * 2,
        fx: 0, fy: 0,
        r: isRoot ? ROOT_R : BASE_R, cr: 0,   // cr = current animated radius
        color: GC[a.group] || GC.misc,
        icon: a.icon || '◈', label: a.label || a.type,
        short: (a.label || a.type || '').slice(0, 14),
        group: a.group, id: a.id, ts: a.ts,
        params: a.params, meta: a.meta,
        replayable: a.replayable,
        phase: Math.random() * PI2, spawn: S.t,
      });
      if (i > 0) {
        S.edges.push({
          a: i - 1, b: i,
          particles: Array.from({ length: 3 }, (_, k) => ({ t: k / 3, sp: 0.0025 + Math.random() * 0.002 })),
        });
      }
      // Spawn ripple
      S.ripples.push({ x: sx, y: sy, r: 0, max: 55 + Math.random() * 25, a: 0.7, color: GC[a.group] || GC.misc });
    }
    S.synced = actionNodes.length;
    // Ambient particles
    while (S.ambient.length < 22) {
      S.ambient.push({
        x: Math.random() * S.w, y: Math.random() * S.h,
        vx: (Math.random() - 0.5) * 0.25, vy: (Math.random() - 0.5) * 0.25,
        sz: Math.random() * 1.4 + 0.4, al: Math.random() * 0.25 + 0.08,
      });
    }
  }, [actionNodes, activeModule, S]);

  // ── Handle session clear ────────────────────────────────────────────────
  const prevLen = useRef(actionNodes.length);
  useEffect(() => {
    if (actionNodes.length === 0 && prevLen.current > 0) {
      S.nodes = []; S.edges = []; S.ripples = []; S.synced = 0;
    }
    prevLen.current = actionNodes.length;
  }, [actionNodes.length, S]);

  // ── Animation loop ──────────────────────────────────────────────────────
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');

    function resize() {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const dpr = window.devicePixelRatio || 1;
      const w = rect.width, h = CANVAS_H;
      cvs.width = w * dpr; cvs.height = h * dpr;
      cvs.style.width = w + 'px'; cvs.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      S.w = w; S.h = h;
    }
    resize();
    window.addEventListener('resize', resize);

    function frame() {
      animRef.current = requestAnimationFrame(frame);
      S.t += 0.016;
      const { nodes, edges, ripples, ambient, mouse, w, h, t } = S;
      const cx = w / 2, cy = h / 2, N = nodes.length;

      // ── Physics ──────────────────────────────────────────────────────────
      for (let i = 0; i < N; i++) { nodes[i].fx = 0; nodes[i].fy = 0; }
      // Center gravity
      for (let i = 0; i < N; i++) {
        nodes[i].fx += (cx - nodes[i].x) * PH.gravity;
        nodes[i].fy += (cy - nodes[i].y) * PH.gravity;
      }
      // Repulsion
      for (let i = 0; i < N; i++) for (let j = i + 1; j < N; j++) {
        const dx = nodes[j].x - nodes[i].x, dy = nodes[j].y - nodes[i].y;
        const d2 = dx * dx + dy * dy + 1, d = Math.sqrt(d2);
        const F = PH.repel / d2;
        const fx = (dx / d) * F, fy = (dy / d) * F;
        nodes[i].fx -= fx; nodes[i].fy -= fy;
        nodes[j].fx += fx; nodes[j].fy += fy;
      }
      // Edge springs
      for (const e of edges) {
        const na = nodes[e.a], nb = nodes[e.b];
        if (!na || !nb) continue;
        const dx = nb.x - na.x, dy = nb.y - na.y;
        const d = Math.sqrt(dx * dx + dy * dy) + 0.1;
        const F = PH.springK * (d - PH.springLen);
        const fx = (dx / d) * F, fy = (dy / d) * F;
        na.fx += fx; na.fy += fy; nb.fx -= fx; nb.fy -= fy;
      }
      // Integrate
      for (let i = 0; i < N; i++) {
        const n = nodes[i];
        if (mouse.down && mouse.drag === i) {
          n.x += (mouse.x - n.x) * 0.35; n.y += (mouse.y - n.y) * 0.35;
          n.vx = 0; n.vy = 0; continue;
        }
        n.vx = clamp((n.vx + n.fx) * PH.damp, -PH.maxV, PH.maxV);
        n.vy = clamp((n.vy + n.fy) * PH.damp, -PH.maxV, PH.maxV);
        n.x += n.vx; n.y += n.vy;
        const m = n.r + 6;
        n.x = clamp(n.x, m, w - m); n.y = clamp(n.y, m, h - m);
        n.cr += (n.r - n.cr) * 0.09; // spawn grow
      }
      // Edge particles
      for (const e of edges) for (const p of e.particles) { p.t += p.sp; if (p.t > 1) p.t -= 1; }
      // Ambient
      for (const p of ambient) {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
      }
      // Ripples
      for (let i = ripples.length - 1; i >= 0; i--) {
        ripples[i].r += 1.8; ripples[i].a *= 0.955;
        if (ripples[i].a < 0.015) ripples.splice(i, 1);
      }

      // ── Render ───────────────────────────────────────────────────────────
      ctx.clearRect(0, 0, w, h);
      // BG subtle radial
      const bg = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.7);
      bg.addColorStop(0, h2r(accentHex, 0.025)); bg.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, w, h);
      // Dot grid
      ctx.fillStyle = 'rgba(100,116,139,0.06)';
      for (let gx = 14; gx < w; gx += 26) for (let gy = 14; gy < h; gy += 26) ctx.fillRect(gx, gy, 1, 1);
      // Ambient particles
      for (const p of ambient) {
        ctx.beginPath(); ctx.arc(p.x, p.y, p.sz, 0, PI2);
        ctx.fillStyle = `rgba(34,211,238,${p.al * 0.4})`; ctx.fill();
      }

      if (N === 0) { drawEmpty(ctx, w, h, t, accentHex); return; }

      // Ripples
      for (const rp of ripples) {
        ctx.beginPath(); ctx.arc(rp.x, rp.y, rp.r, 0, PI2);
        ctx.strokeStyle = h2r(rp.color, rp.a); ctx.lineWidth = 2; ctx.stroke();
      }

      // Edges
      const hov = mouse.hover;
      for (const e of edges) {
        const na = nodes[e.a], nb = nodes[e.b];
        if (!na || !nb || na.cr < 1 || nb.cr < 1) continue;
        const hi = hov === e.a || hov === e.b;
        const alpha = hi ? 0.45 : 0.14;
        const dx = nb.x - na.x, dy = nb.y - na.y;
        const mx = (na.x + nb.x) / 2 + dy * 0.18, my = (na.y + nb.y) / 2 - dx * 0.18;
        // Glow
        if (hi) {
          ctx.beginPath(); ctx.moveTo(na.x, na.y); ctx.quadraticCurveTo(mx, my, nb.x, nb.y);
          ctx.strokeStyle = h2r(na.color, 0.08); ctx.lineWidth = 7; ctx.stroke();
        }
        // Line
        ctx.beginPath(); ctx.moveTo(na.x, na.y); ctx.quadraticCurveTo(mx, my, nb.x, nb.y);
        ctx.strokeStyle = h2r(na.color, alpha); ctx.lineWidth = hi ? 1.8 : 1; ctx.stroke();
        // Arrow
        const tx = nb.x - mx, ty = nb.y - my, td = Math.hypot(tx, ty) || 1;
        const ax = tx / td, ay = ty / td;
        const tipX = nb.x - ax * (nb.cr + 3), tipY = nb.y - ay * (nb.cr + 3);
        const wl = 5, wa = 0.5;
        ctx.beginPath();
        ctx.moveTo(tipX - wl * (ax * Math.cos(wa) - ay * Math.sin(wa)), tipY - wl * (ay * Math.cos(wa) + ax * Math.sin(wa)));
        ctx.lineTo(tipX, tipY);
        ctx.lineTo(tipX - wl * (ax * Math.cos(wa) + ay * Math.sin(wa)), tipY - wl * (ay * Math.cos(wa) - ax * Math.sin(wa)));
        ctx.strokeStyle = h2r(na.color, alpha * 1.6); ctx.lineWidth = 1.2; ctx.stroke();
        // Flowing particles
        for (const p of e.particles) {
          const pt = p.t;
          const px = (1 - pt) * (1 - pt) * na.x + 2 * (1 - pt) * pt * mx + pt * pt * nb.x;
          const py = (1 - pt) * (1 - pt) * na.y + 2 * (1 - pt) * pt * my + pt * pt * nb.y;
          const pr = hi ? 2.4 : 1.5;
          const pg = ctx.createRadialGradient(px, py, 0, px, py, pr * 2.5);
          pg.addColorStop(0, h2r(na.color, hi ? 0.85 : 0.6));
          pg.addColorStop(1, h2r(na.color, 0));
          ctx.beginPath(); ctx.arc(px, py, pr * 2.5, 0, PI2); ctx.fillStyle = pg; ctx.fill();
          ctx.beginPath(); ctx.arc(px, py, pr, 0, PI2);
          ctx.fillStyle = h2r(na.color, 0.9); ctx.fill();
        }
      }

      // Nodes
      for (let i = 0; i < N; i++) {
        const n = nodes[i];
        if (n.cr < 0.5) continue;
        const isH = hov === i;
        const isD = mouse.down && mouse.drag === i;
        const breathe = Math.sin(t * 1.8 + n.phase) * 0.05 + 1;
        const r = n.cr * (isH ? 1.35 : 1) * breathe;

        // Outer glow layers
        for (let g = 4; g >= 1; g--) {
          ctx.beginPath(); ctx.arc(n.x, n.y, r + g * (isH ? 7 : 4.5), 0, PI2);
          ctx.fillStyle = h2r(n.color, (isH ? 0.10 : 0.04) / g); ctx.fill();
        }
        // Main gradient orb
        const gr = ctx.createRadialGradient(n.x - r * 0.28, n.y - r * 0.28, r * 0.08, n.x, n.y, r);
        gr.addColorStop(0, h2r(n.color, 0.92));
        gr.addColorStop(0.45, h2r(n.color, 0.5));
        gr.addColorStop(1, h2r(n.color, 0.12));
        ctx.beginPath(); ctx.arc(n.x, n.y, r, 0, PI2); ctx.fillStyle = gr; ctx.fill();
        // Border ring
        ctx.strokeStyle = h2r(n.color, isH ? 0.9 : 0.45);
        ctx.lineWidth = isH ? 2 : 1.2; ctx.stroke();
        // Specular highlight
        const sp = ctx.createRadialGradient(n.x - r * 0.32, n.y - r * 0.38, 0, n.x, n.y, r);
        sp.addColorStop(0, 'rgba(255,255,255,0.22)'); sp.addColorStop(0.45, 'rgba(255,255,255,0)');
        ctx.beginPath(); ctx.arc(n.x, n.y, r, 0, PI2); ctx.fillStyle = sp; ctx.fill();
        // Emoji icon
        ctx.font = `${Math.max(11, r * 0.62)}px serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(n.icon, n.x, n.y + 1);
        // Label
        if (isH || N <= 10) {
          ctx.font = `700 ${clamp(r * 0.32, 6.5, 8.5)}px ${MONO}`;
          ctx.fillStyle = h2r(n.color, 0.88); ctx.textAlign = 'center';
          ctx.fillText(n.short, n.x, n.y + r + (isH ? 14 : 11));
        }
        // Hover ring (animated dash)
        if (isH) {
          ctx.beginPath(); ctx.arc(n.x, n.y, r + 5, 0, PI2);
          ctx.strokeStyle = h2r(n.color, 0.4 + Math.sin(t * 5) * 0.25);
          ctx.lineWidth = 1.5; ctx.setLineDash([3.5, 4]); ctx.lineDashOffset = -t * 32;
          ctx.stroke(); ctx.setLineDash([]);
        }
        // Drag ring
        if (isD) {
          ctx.beginPath(); ctx.arc(n.x, n.y, r + 9, 0, PI2);
          ctx.strokeStyle = h2r(n.color, 0.55); ctx.lineWidth = 2;
          ctx.setLineDash([2, 3.5]); ctx.lineDashOffset = t * 45;
          ctx.stroke(); ctx.setLineDash([]);
        }
        // Spawn pulse ring
        const age = t - n.spawn;
        if (age < 1.8) {
          const pa = Math.max(0, 1 - age / 1.8) * 0.5;
          ctx.beginPath(); ctx.arc(n.x, n.y, r + age * 25, 0, PI2);
          ctx.strokeStyle = h2r(n.color, pa); ctx.lineWidth = 2; ctx.stroke();
        }
        // Replayable inner badge
        if (n.replayable) {
          ctx.beginPath(); ctx.arc(n.x + r * 0.62, n.y - r * 0.62, 3.5, 0, PI2);
          ctx.fillStyle = '#4ade80'; ctx.fill();
          ctx.strokeStyle = '#0a0f1a'; ctx.lineWidth = 1.2; ctx.stroke();
        }
      }
    }

    animRef.current = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(animRef.current); window.removeEventListener('resize', resize); };
  }, [accentHex, S]);

  // ── Mouse / Touch handlers ─────────────────────────────────────────────
  const nodeAt = useCallback((mx, my) => {
    const nodes = S.nodes;
    for (let i = nodes.length - 1; i >= 0; i--) {
      if (Math.hypot(mx - nodes[i].x, my - nodes[i].y) < nodes[i].cr * 1.4) return i;
    }
    return -1;
  }, [S]);

  const getPos = useCallback((e) => {
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    const touch = e.touches?.[0];
    return { x: (touch || e).clientX - r.left, y: (touch || e).clientY - r.top };
  }, []);

  const onPointerDown = useCallback((e) => {
    const { x, y } = getPos(e);
    const idx = nodeAt(x, y);
    S.mouse.down = true; S.mouse.drag = idx; S.mouse.x = x; S.mouse.y = y;
    if (idx >= 0) { canvasRef.current.style.cursor = 'grabbing'; setTooltip(null); }
  }, [nodeAt, getPos, S]);

  const onPointerMove = useCallback((e) => {
    const { x, y } = getPos(e);
    S.mouse.x = x; S.mouse.y = y;
    if (!S.mouse.down) {
      const idx = nodeAt(x, y);
      S.mouse.hover = idx;
      canvasRef.current.style.cursor = idx >= 0 ? 'grab' : 'default';
      if (idx >= 0) {
        const n = S.nodes[idx];
        setTooltip({ x, y, label: n.label, icon: n.icon, time: formatRelTime(n.ts), color: n.color, params: n.params, meta: n.meta, replayable: n.replayable });
      } else { setTooltip(null); }
    }
  }, [nodeAt, getPos, S]);

  const onPointerUp = useCallback(() => {
    if (S.mouse.drag >= 0) {
      const n = S.nodes[S.mouse.drag];
      if (n) { n.vx = (Math.random() - 0.5) * 1.5; n.vy = (Math.random() - 0.5) * 1.5; }
    }
    S.mouse.down = false; S.mouse.drag = -1;
    if (canvasRef.current) canvasRef.current.style.cursor = 'default';
  }, [S]);

  const onPointerLeave = useCallback(() => {
    S.mouse.hover = -1; S.mouse.down = false; S.mouse.drag = -1;
    setTooltip(null);
  }, [S]);

  // ── Actions ─────────────────────────────────────────────────────────────
  const handleClear = useCallback(() => {
    const full = loadSession(userKey);
    const remaining = (full.nodes || []).filter((n) => n.module !== activeModule);
    saveSession(userKey, { ...full, nodes: remaining });
    onSessionChange?.();
  }, [userKey, activeModule, onSessionChange]);

  const handleSave = useCallback(() => {
    const tree = dagTree.find(d => d.module === activeModule);
    if (!tree?.runs?.length) return;
    const run = tree.runs[tree.runs.length - 1];
    const name = `Pipeline ${new Date().toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}`;
    const pipeline = extractPipeline(run, name, '', activeModule);
    savePipeline(userKey, pipeline);
  }, [dagTree, activeModule, userKey]);

  const hasReplayable = actionNodes.some(n => n.replayable);
  const nodeCount = actionNodes.length;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 11 }}>{moduleInfo.icon}</span>
          <span style={{ fontSize: 8, color: accentHex, fontFamily: MONO, fontWeight: 800, letterSpacing: '.1em' }}>
            LIVE GRAPH
          </span>
          {nodeCount > 0 && (
            <span style={{ fontSize: 7.5, color: T.t4, fontFamily: MONO, background: h2r(accentHex, 0.08), border: `1px solid ${h2r(accentHex, 0.15)}`, padding: '1px 5px', borderRadius: 2 }}>
              {nodeCount}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {hasReplayable && (
            <button onClick={handleSave} style={{ fontSize: 7.5, color: T.green, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: MONO, fontWeight: 700 }}>SAVE</button>
          )}
          {nodeCount > 0 && (
            <button onClick={handleClear} style={{ fontSize: 7.5, color: T.red, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: MONO }}>CLEAR</button>
          )}
        </div>
      </div>

      {/* Canvas */}
      <div ref={containerRef} style={{ position: 'relative', borderRadius: 4, overflow: 'hidden', border: `1px solid ${T.b0}`, background: T.void }}>
        <canvas
          ref={canvasRef}
          style={{ display: 'block', width: '100%', height: CANVAS_H, touchAction: 'none' }}
          onMouseDown={onPointerDown} onMouseMove={onPointerMove}
          onMouseUp={onPointerUp} onMouseLeave={onPointerLeave}
          onTouchStart={onPointerDown} onTouchMove={onPointerMove}
          onTouchEnd={onPointerUp}
        />

        {/* Tooltip overlay */}
        {tooltip && (
          <div style={{
            position: 'absolute', pointerEvents: 'none', zIndex: 10,
            left: clamp(tooltip.x - 70, 4, (S.w || 300) - 160), top: clamp(tooltip.y - 90, 4, CANVAS_H - 60),
            width: 150, padding: '8px 10px',
            background: 'rgba(10,15,26,0.92)', border: `1px solid ${h2r(tooltip.color, 0.4)}`,
            borderRadius: 5, backdropFilter: 'blur(6px)',
            boxShadow: `0 0 18px ${h2r(tooltip.color, 0.15)}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
              <span style={{ fontSize: 12 }}>{tooltip.icon}</span>
              <span style={{ fontSize: 9, color: tooltip.color, fontWeight: 700, fontFamily: MONO, lineHeight: 1.3, wordBreak: 'break-word' }}>{tooltip.label}</span>
            </div>
            <div style={{ fontSize: 7.5, color: T.t4, fontFamily: MONO, marginBottom: 3 }}>{tooltip.time}</div>
            {tooltip.replayable && (
              <div style={{ marginBottom: 3 }}>
                <span style={{ fontSize: 7, color: '#4ade80', background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.25)', borderRadius: 2, padding: '1px 4px', fontFamily: MONO }}>replayable</span>
              </div>
            )}
            {Object.entries({ ...(tooltip.params || {}), ...(tooltip.meta || {}) })
              .filter(([, v]) => v !== undefined && v !== null && v !== '')
              .slice(0, 3)
              .map(([k, v]) => (
                <div key={k} style={{ fontSize: 7.5, fontFamily: MONO, lineHeight: 1.6 }}>
                  <span style={{ color: T.t4 }}>{k}: </span>
                  <span style={{ color: tooltip.color }}>{String(v).slice(0, 30)}</span>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Legend */}
      {nodeCount > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6, padding: '0 2px' }}>
          {[...new Set(actionNodes.map(n => n.group))].map(g => (
            <div key={g} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 5px', background: h2r(GC[g] || GC.misc, 0.06), border: `1px solid ${h2r(GC[g] || GC.misc, 0.15)}`, borderRadius: 2 }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: GC[g] || GC.misc }} />
              <span style={{ fontSize: 7, color: T.t3, fontFamily: MONO, textTransform: 'uppercase' }}>{g}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
